import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import VoiceDirectory from "@/components/VoiceDirectory";
import NotFound from "@/pages/NotFound";
import { getChildVoice, CHILD_VOICES } from "@/config/childVoices";
import { ChevronRight, ArrowRight } from "lucide-react";

export default function ChildVoiceLanguage() {
  const { slug } = useParams();
  const cfg = getChildVoice(slug);

  // Keep the directory `initial` object stable so it only re-scopes on slug change.
  const initial = useMemo(() => (cfg ? { language: cfg.language } : {}), [cfg && cfg.language]);

  if (!cfg) return <NotFound />;

  const canonicalPath = `/child-voices/${slug}`;
  const base = "https://www.voiceoverkids.agency";

  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "CollectionPage",
      name: cfg.h1, url: base + canonicalPath, description: cfg.description,
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: base + "/" },
        { "@type": "ListItem", position: 2, name: "Child Voices", item: base + "/child-voices" },
        { "@type": "ListItem", position: 3, name: `${cfg.displayName} Kids Voices`, item: base + canonicalPath },
      ],
    },
  ];

  const related = (cfg.related || []).map((s) => ({ slug: s, ...CHILD_VOICES[s] })).filter((r) => r.h1);

  return (
    <>
      <SEO title={cfg.title} path={canonicalPath} description={cfg.description} jsonLd={jsonLd} />

      <section className="bg-brand-ink text-white">
        <div className="container-x py-12 md:py-14">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="mb-4 flex flex-wrap items-center gap-1.5 text-sm text-zinc-400">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={13} />
            <Link to="/child-voices" className="hover:text-white">Child Voices</Link>
            <ChevronRight size={13} />
            <span className="text-zinc-200">{cfg.displayName} Kids Voices</span>
          </nav>

          <span className="label-eyebrow text-brand-purple">Child &amp; kids voices</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter md:text-5xl">{cfg.h1}</h1>
          <p className="mt-3 max-w-2xl text-zinc-300">{cfg.intro}</p>
        </div>
      </section>

      <div className="container-x -mt-8 pb-4">
        <VoiceDirectory initial={initial} />
      </div>

      {/* Additional SEO content + contextual internal links */}
      <section className="bg-white border-t border-zinc-200 py-14">
        <div className="container-x max-w-3xl">
          {cfg.extra && <p className="text-zinc-600 leading-relaxed">{cfg.extra}</p>}

          {related.length > 0 && (
            <div className="mt-8">
              <h2 className="font-heading text-lg font-bold text-zinc-900">Related languages &amp; accents</h2>
              <div className="mt-3 flex flex-wrap gap-3">
                {related.map((r) => (
                  <Link key={r.slug} to={`/child-voices/${r.slug}`} data-testid={`related-${r.slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 transition hover:border-brand-violet hover:text-brand-violet">
                    {r.h1} <ArrowRight size={14} />
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 rounded-3xl border border-zinc-200 bg-brand-paper p-8 text-center">
            <p className="text-zinc-600">Can't find the right voice? Our international network goes far beyond what's shown online.</p>
            <Link to="/casting" className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-violet px-6 py-3 font-bold text-white transition hover:bg-brand-violetHover">
              Ask us to cast for you <ArrowRight size={16} />
            </Link>
          </div>

          <p className="mt-8 text-sm text-zinc-500">
            <Link to="/child-voices" className="font-semibold text-brand-violet hover:underline">Browse all kids voice over artists by language →</Link>
          </p>
        </div>
      </section>
    </>
  );
}
